import { useQuery } from "@tanstack/react-query";
import { engineError } from "@/lib/engine-error";
import type { AggregateRiskEnvelope, RiskEnvelope } from "@/lib/profitability-api";

/**
 * Projects heading past their hours budget, or working past their end date.
 *
 * No paging: the board is three buckets that have to be read together -- the
 * priced ranking, the over-budget demands nobody can price, and the demands
 * neither risk could be evaluated on. Paging the first would leave the other
 * two stranded, and the whole set is ~140 rows.
 *
 * An Aggregate caller gets a different, percentage-only shape for their own
 * book (`isAggregateRisk`); the page renders it with its own board.
 */
export function useRisk(range?: string) {
  return useQuery({
    queryKey: ["risk", range ?? "default"],
    queryFn: async (): Promise<RiskEnvelope | AggregateRiskEnvelope> => {
      const params = new URLSearchParams();
      if (range) params.set("range", range);
      const qs = params.toString();
      const response = await fetch(`/api/risk${qs ? `?${qs}` : ""}`);
      if (!response.ok) {
        throw await engineError(response, "Could not load the risk board");
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Which board a risk response is for. Here, not beside the types in
 * profitability-api.ts: that module is server-side (it reads the service token
 * from the environment) and must not be pulled into the browser bundle.
 */
export function isAggregateRisk(
  data: RiskEnvelope | AggregateRiskEnvelope,
): data is AggregateRiskEnvelope {
  return "access" in data && data.access !== undefined;
}
